import history from "../routes/history";
import api from "./api";
import {calculatePaymentFee} from "../screens/ConfirmPaymentScreen/functions/PaymentFeeHelper";

export const elementInViewport = (el) => {
    try {
        let top = el.offsetTop;
        let left = el.offsetLeft;
        let width = el.offsetWidth;
        let height = el.offsetHeight;

        while (el.offsetParent) {
            el = el.offsetParent;
            top += el.offsetTop;
            left += el.offsetLeft;
        }

        return (
            top >= window.pageYOffset &&
            left >= window.pageXOffset &&
            (top + height) <= (window.pageYOffset + window.innerHeight) &&
            (left + width) <= (window.pageXOffset + window.innerWidth)
        );
    } catch (e) {
        return false;
    }
};


// A Magento order entity_id (used to build the REST URL) is always a plain integer.
const isValidEntityId = (id) =>
    (typeof id === 'number' && Number.isFinite(id)) ||
    (typeof id === 'string' && /^\d+$/.test(id.trim()));

// An increment_id (order number) is a non-empty string/number scalar. Its format varies by
// store config (e.g. "1003687285-de_a"), so it must NOT be restricted to digits only -
// it just needs to not be an object/array/empty value (the original [object Object] bug).
const isSafeScalar = (value) =>
    (typeof value === 'number' && Number.isFinite(value)) ||
    (typeof value === 'string' && value.trim().length > 0 && value.trim() !== '[object Object]');

export const HandleSuccessOrder = async (globalState, globalActions, orderId = null, redirect = true, orderIdIsIncrement = false) => {
    try {
        if (window.dataLayer && globalState?.checkout?.totals?.base_grand_total) {
            // Call API to/rest/default/V1/calcuso-core/order/1568752/increment-id to get increment_id
            let increment_id = '';
            if (orderIdIsIncrement) {
                // orderId is already an increment_id (e.g. from a URL query param) - validate loosely.
                increment_id = isSafeScalar(orderId) ? orderId : null;
            } else if (!isValidEntityId(orderId)) {
                console.error("HandleSuccessOrder: orderId is not a valid entity id, skipping increment-id lookup", orderId);
                increment_id = null;
            } else {
                try {
                    let response = await api.get(`/calcuso-core/order/${orderId}/increment-id`);
                    increment_id = isSafeScalar(response.data) ? response.data : null;
                } catch (e) {
                    console.error("Error getting increment_id:", e);
                    increment_id = orderId; // orderId is already confirmed a valid entity id at this point
                }
            }
            const {groupOrder, checkout} = globalState;
            const {configs} = groupOrder;
            const {totals, payment} = checkout;
            let paymentFee = calculatePaymentFee(configs, payment, totals?.base_grand_total);
            let total = globalState.checkout.totals.base_grand_total + paymentFee;
            total = Math.round((total + Number.EPSILON) * 100) / 100;
            window.dataLayer.push({
                event: 'calcuso-go-payment-success',
                page_path: '/calcuso-go-payment-success',
                page_title: 'GO | Product Payment Success',
                subtotal_incl_tax: total,
                ecommerce: {
                    transaction_id: increment_id ? increment_id : Math.floor(1e13 + Math.random() * 9e13), // Random ID if null
                    value: total,
                    currency: 'EUR', // Google Ads braucht oft die Währung explizit
                    subtotal_incl_tax: total
                }
            });
        }
    } catch (e) {
        console.error("Error pushing to dataLayer:", e);
    }
    const groupOrder = globalState.groupOrder;
    const school_code = groupOrder?.school_code || globalState?.groupOrder?.school_code;
    globalActions.initCheckout();
    globalActions.initGroupOrder();
    globalActions.initFakeCart();
    deleteCookie('calcuso_attribution');
    if (redirect) {
        history.push(`/group-orders/${school_code.trim()}/checkout/done`);
    }
}

export const isOutOfStock = (product) => {
    if (product && product.product_type === 'configurable' && (!product.product_simples || product.product_simples.length === 0)) {
        return true;
    }

    return false;
}

export const isPackageOutOfStock = (packageProducts) => {
    if (!packageProducts || packageProducts.length === 0) {
        return true;
    }

    return packageProducts.some(product => isOutOfStock(product));
}


/**
 * Set a cookie with the given name, value, and expiration days
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Number of days until expiration (default: 90)
 */
export const setCookie = (name, value, days = 90) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
}

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
export const getCookie = (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
}

/**
 * Delete a cookie by name
 * @param {string} name - Cookie name
 */
export const deleteCookie = (name) => {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
}

/**
 * Tracking parameters to check for marketing attribution
 */
const TRACKING_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gad_campaignid',
    'gclid',
    'fbclid',
    'msclkid',
    'ttclid'
];

/**
 * Brand campaign IDs that should not overwrite last_nondirect_touch if it already exists
 */
const CAMPAIGN_BRANDS = [
    // Add brand campaign IDs here if needed
    // Example: '1234567890'
    // Will have handle in server side
];

/**
 * Extract calcuso_attribution from URL and process it according to PHP logic
 */
export const setCalcusoAttributionCookie = () => {
    try {
        const timestamp = Math.floor(Date.now() / 1000);
        const url = window.location.href;

        // Get all URL params (from both query string and hash)
        const urlBeforeHash = url.split('#')[0];
        const urlParams = new URLSearchParams(urlBeforeHash.split('?')[1] || '');

        // Also check hash params
        if (url.includes('#')) {
            const hashPart = url.split('#')[1];
            if (hashPart && hashPart.includes('?')) {
                const hashParams = new URLSearchParams(hashPart.split('?')[1]);
                hashParams.forEach((value, key) => {
                    if (!urlParams.has(key)) {
                        urlParams.set(key, value);
                    }
                });
            }
        }

        // Check if calcuso_attribution is already in URL (legacy support)
        let calcusoAttribution = urlParams.get('calcuso_attribution');
        if (calcusoAttribution) {
            try {
                const parsed = JSON.parse(decodeURIComponent(calcusoAttribution));
                if (parsed && typeof parsed === 'object') {
                    setCookie('calcuso_attribution', JSON.stringify(parsed), 90);
                    return;
                }
            } catch (e) {
                // If parsing fails, continue with normal processing
            }
        }

        // Collect tracking parameters
        const incomingData = {};
        let isMarketingHit = false;

        TRACKING_PARAMS.forEach(param => {
            const value = urlParams.get(param);
            if (value && value !== '') {
                // HTML escape similar to htmlspecialchars in PHP
                incomingData[param] = value
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&#039;');
                isMarketingHit = true;
            }
        });

        // Get existing cookie data
        const existingCookie = getCookie('calcuso_attribution');
        let data = {};

        if (existingCookie && existingCookie !== '') {
            try {
                const decoded = JSON.parse(existingCookie);
                if (decoded && typeof decoded === 'object') {
                    data = decoded;
                }
            } catch (e) {
                // If parsing fails, start with empty data
            }
        }

        // Check if it's a brand campaign
        const gadCampaignId = incomingData['gad_campaignid'] || '';
        const isBrandCampaign = CAMPAIGN_BRANDS.includes(gadCampaignId);

        // Update last_touch
        if (isMarketingHit) {
            data['last_touch'] = {
                ...incomingData,
                type: 'marketing',
                timestamp: timestamp
            };
        } else {
            data['last_touch'] = {
                source: 'direct',
                timestamp: timestamp
            };
        }

        // Update last_nondirect_touch
        if (isMarketingHit) {
            if (!isBrandCampaign) {
                // Not a brand campaign -> always update
                data['last_nondirect_touch'] = {
                    ...incomingData,
                    timestamp: timestamp
                };
            } else {
                // Brand campaign -> only update if empty
                if (!data['last_nondirect_touch'] || Object.keys(data['last_nondirect_touch']).length === 0) {
                    data['last_nondirect_touch'] = {
                        ...incomingData,
                        timestamp: timestamp
                    };
                }
            }
        }

        // Update first_touch (only if empty)
        if (!data['first_touch'] || Object.keys(data['first_touch']).length === 0) {
            if (isMarketingHit) {
                data['first_touch'] = {
                    ...incomingData,
                    timestamp: timestamp,
                    type: 'marketing'
                };
            } else {
                data['first_touch'] = {
                    source: 'direct',
                    timestamp: timestamp
                };
            }
        }

        // Save to cookie with 90 days expiration
        setCookie('calcuso_attribution', JSON.stringify(data), 90);
    } catch (error) {
        console.error('Error setting calcuso_attribution cookie:', error);
    }
}

export const cleanCalcusoAttributionParamOnUrl = () => {
    try {
        const href = window.location.href;

        console.log(href)
        // Split URL into main part and hash
        const [beforeHash, rawHash = ""] = href.split('#');

        // Process query params in the main URL
        const [baseUrl, queryString = ""] = beforeHash.split('?');
        const queryParams = new URLSearchParams(queryString);
        const hadMainParam = queryParams.has('calcuso_attribution');
        if (hadMainParam) {
            queryParams.delete('calcuso_attribution');
        }

        // Process query params inside the hash (if any), e.g., #/route?calcuso_attribution=...
        let newHash = rawHash;
        let hadHashParam = false;
        if (rawHash) {
            const [hashPath, hashQuery = ""] = rawHash.split('?');
            if (hashQuery) {
                const hashParams = new URLSearchParams(hashQuery);
                if (hashParams.has('calcuso_attribution')) {
                    hashParams.delete('calcuso_attribution');
                    hadHashParam = true;
                }
                const newHashQuery = hashParams.toString();
                newHash = newHashQuery ? `${hashPath}?${newHashQuery}` : hashPath;
            }
        }

        // Only update the URL if something changed
        if (hadMainParam || hadHashParam) {
            const newQuery = queryParams.toString();
            const rebuilt = newQuery ? `${baseUrl}?${newQuery}` : baseUrl;
            const finalUrl = newHash ? `${rebuilt}#${newHash}` : rebuilt;
            window.history.replaceState({}, document.title, finalUrl);
        }
    } catch (e) {
        console.error('Error cleaning calcuso_attribution from URL:', e);
    }
}
